<template>
  <div class="page">
    <div
      v-if="showContentLoader"
      class="loader-wrapper"
    >
      <tiny-loader
        class="loader-class"
      />
    </div>
    <div
      v-else
      ref="storyContentRef"
      style="height: 100%"
    >
      <page-viewer
        v-for="(page, index) in storyPages"
        v-show="index === currentPageIndex"
        :key="index"
        :page-content="page.content"
        :page-id="index"
        :class="index === currentPageIndex ? 'active' : ''"
        @user-interaction="onUserInteraction"
      />
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    text="Failed to connect your account."
    bold-text="Please try connecting again."
    reason="truelayer_authentication_failed"
    @close="showErrorToast = false"
    @bold-text-click="handleConnectAccountsClick"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import { onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getCustomerCredentials } from '../../api/cashflowApi';
import { getOtsPages } from '../../api/sumsumApi';
import {
  authenticateTrueLayerCode,
  getTrueLayerUrl,
  getStripeUrl,
  createStripeCreds,
} from '../../api/trueLayer';
import useContentLoader from '../../composables/useContentLoader';
import config from '../../config';
import Segment from '../../Segment';
import { useOtsJob } from '../../services/otsConfigService';
import { useStripe } from '../../services/stripeService';
import { useSessionStore } from '../../stores/session';
import { OtsPageType } from '../../types/otsConfig';
import ErrorToast from '../Common/ErrorToast.vue';
import storyLib from '../StoryLib';

const { PageViewer } = storyLib;

const sessionStore = useSessionStore();
const storyContentRef = ref(null); // Fixed duplicate ref
const route = useRoute();
const { getOtsJobConfig, getOtsFlowConfig, startPollingByFlowConfigId, isPolling } = useOtsJob();
const { getStripe } = useStripe();
const storyPages = ref([]);
const currentPageIndex = ref(0);
const story = ref(null);
const router = useRouter();
const otsJobConfig = await getOtsJobConfig();
const otsFlowConfig = await getOtsFlowConfig();
const {
  showContentLoader, toggleLoader,
} = useContentLoader();
const showErrorToast = ref(false);

const onUserInteraction = async data => {
  if (data && data.eventName === 'linkClicked' && data.link) {
    Segment.trackUserGot('OTSConnect_LinkClicked', { otsJobConfig, otsFlowConfig, link: data.link });
    window.open(data.link, '_blank');
  }

  if (data && data.eventName === 'nextPage') {
    nextPage();
  }

  if (data && data.eventName === 'connectAccountsClicked') {
    await handleConnectAccountsClick();
  }

  if (data && data.eventName === 'connectAccountsClicked_Stripe') {
    await handleConnectAccountsClickStripe();
  }
};

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('OTSConnect_ConnectAccountsClicked', { otsJobConfig, otsFlowConfig });
  window.location.href = await getTrueLayerUrl(
    { jobId: otsJobConfig.jobId, fid: otsFlowConfig.flowId },
    config.get().otsEnv,
  );
};

const handleConnectAccountsClickStripe = async () => {
  Segment.trackUserGot('OTSConnect_ConnectAccountsClicked_Stripe', { otsJobConfig, otsFlowConfig });

  const stripeConnectionSessionSecret = await getStripeUrl(
    { jobId: otsJobConfig.jobId, fid: otsFlowConfig.flowId },
    config.get().otsEnv,
  );
  const stripe = await getStripe();

  const res = await stripe.collectFinancialConnectionsAccounts({
    clientSecret: stripeConnectionSessionSecret,
  });

  if (res && res.financialConnectionsSession.accounts.length > 0) {
    toggleLoader(true);
    await createStripeCreds(res.financialConnectionsSession.accounts);
    await reloadStory();
  }
};

const handlePollingError = () => {
  if (otsFlowConfig.connectAccountsFlow.pageType === OtsPageType.url) {
    router.push(otsFlowConfig.connectAccountsFlow.flowId);
  } else {
    router.push('/ots/connect');
  }
};

const pollingSuccess = async () => {
  if (otsFlowConfig && otsFlowConfig.resultsPageFlow.pageType === OtsPageType.url) {
    router.push(otsFlowConfig.resultsPageFlow.flowId);
  } else if (otsFlowConfig && otsFlowConfig.resultsPageFlow.pageType === OtsPageType.story) {
    router.push('/ots/success');
  } else {
    router.push('/ots/etoro/success');
  }
};

const nextPage = async () => {
  // If current page is the last page, go directly to success
  if (currentPageIndex.value === storyPages.value.length - 1) {
    Segment.trackUserGot(
      'OTSConnect_Done',
      {
        otsJobConfig,
        otsFlowConfig,
      },
    );
    router.push('/ots/success');
  } else if (currentPageIndex.value === storyPages.value.length - 2) { // If current page is one before the last page, start polling
    Segment.trackUserGot(
      'OTSConnect_GoToLoaderClicked',
      {
        otsJobConfig,
        otsFlowConfig,
      },
    );
    startPollingByFlowConfigId(otsFlowConfig.resultsPageFlow.flowId, 300, 3000, pollingSuccess, handlePollingError);
    currentPageIndex.value += 1;
  } else {
    Segment.trackUserGot(
      'OTSConnect_NextPageClicked',
      {
        otsJobConfig,
        otsFlowConfig,
        nextPage: storyPages.value[currentPageIndex.value + 1].name,
      },
    );
    currentPageIndex.value += 1;
  }
};

const reloadStory = async () => {
  toggleLoader(true);
  const creds = await getCustomerCredentials();

  story.value = await getOtsPages(
    otsFlowConfig.connectAccountsFlow.flowId,
    {
      hasCreds: creds.length > 0,
      noCreds: creds.length === 0,
      isLoading: isPolling.value,
    },
  );

  if (story.value) {
    const storyData = typeof story.value === 'string'
      ? JSON.parse(story.value)
      : story.value;

    storyPages.value = storyData.pages || [];
  }
  toggleLoader(false);
};

const verifyTrueLayeToken = async code => {
  if (code) {
    try {
      const response = await authenticateTrueLayerCode(code);
      if (!response) {
        showErrorToast.value = true;
      }
    } catch (error) {
      showErrorToast.value = true;
      console.error('TrueLayer authentication failed:', error);
    }
  }
};

onBeforeMount(async () => {
  const queryParam = route.query || null;

  if (sessionStore.customerId && sessionStore.memberId) {
    Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
  }

  await verifyTrueLayeToken(queryParam.code);

  await reloadStory();

  Segment.trackUserGot('OTSConnect_PageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig, otsFlowConfig });
});

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.page {
  background-color: #E1F1F2;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
